import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, ShoppingCart, Plus, Minus, Package2, Tag, CreditCard, UserPlus, User, Percent, Trash2, Check } from 'lucide-react';
import { StockItem } from '../lib/stockItems';
import { StockCategory } from '../lib/stockCategories';
import { ProductPhotoService } from '../services/productPhotoService';
import RecordProductSalePaymentModal from './RecordProductSalePaymentModal';
import { useBusiness } from '../contexts/BusinessContext';
import { calculateProductSaleTax, createProductSale } from '../lib/productSales';
import { supabase } from '../lib/supabaseClient';
import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import SendInvoiceModal from './SendInvoiceModal';
import { sendGmailEmail } from '../lib/email';
import { Customer } from '../lib/customers';
import SelectCustomerModal from './manage/SelectCustomerModal';
import { DiscountCodeService } from '../services/discountCodeService';

interface CartItem {
  product: StockItem;
  quantity: number;
}

interface SellProductsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Add success callback
  products: StockItem[];
  categories: StockCategory[];
  productPhotos: Record<string, string>;
}

export default function SellProductsModal({
  isOpen,
  onClose,
  onSuccess,
  products,
  categories,
  productPhotos
}: SellProductsModalProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [isFullScreenCart, setIsFullScreenCart] = useState(false);
  const [cartState, setCartState] = useState<'cart' | 'payment'>('cart'); // New state for cart mode
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [showSendInvoiceModal, setShowSendInvoiceModal] = useState(false);
  const [isSendingInvoice, setIsSendingInvoice] = useState(false);
  const [discounts, setDiscounts] = useState<Array<{
    id: string;
    title: string;
    type: 'percentage' | 'fixed';
    amount: number;
  }>>([]);
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [showDiscountCodeForm, setShowDiscountCodeForm] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [validatingCode, setValidatingCode] = useState(false);
  const [codeValidation, setCodeValidation] = useState<{
    isValid: boolean;
    code?: any;
    error?: string;
    discountAmount?: number;
  } | null>(null);
  const [discountForm, setDiscountForm] = useState({
    title: '',
    type: 'percentage' as 'percentage' | 'fixed',
    amount: ''
  });
  
  const { selectedBusiness } = useBusiness();

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Reset state when modal opens
      setSearchQuery('');
      setSelectedCategory('all');
      setCart([]);
      setShowCart(false);
      setIsFullScreenCart(false);
      setCartState('cart');
      setShowPaymentModal(false);
      setSelectedCustomer(null);
      setIsSelectCustomerModalOpen(false);
      setDiscounts([]);
      setShowDiscountForm(false);
      setShowDiscountCodeForm(false);
      setDiscountCode('');
      setValidatingCode(false);
      setCodeValidation(null);
      setDiscountForm({ title: '', type: 'percentage', amount: '' });
    }
  }, [isOpen]);

  // Handle real-time stock updates in the sell modal
  const handleStockUpdate = React.useCallback((payload: RealtimePostgresChangesPayload<StockItem>) => {
    console.log('📡 SellModal: Real-time stock update received:', payload);
    
    if (payload.eventType === 'UPDATE') {
      const updatedItem = payload.new as StockItem;
      console.log('🔄 SellModal: Updating stock item:', updatedItem.name, 'New quantity:', updatedItem.quantity);
      
      // Update the products array that was passed as props
      // Note: This won't update the parent's state, but will show live quantities in the modal
      
      // If the updated item is in the cart and quantity is now 0 or insufficient, adjust cart
      setCart(prevCart => 
        prevCart.map(cartItem => {
          if (cartItem.product.id === updatedItem.id) {
            const maxQuantity = Math.max(0, updatedItem.quantity);
            const adjustedQuantity = Math.min(cartItem.quantity, maxQuantity);
            
            if (adjustedQuantity !== cartItem.quantity) {
              console.log(`⚠️ Adjusting cart quantity for ${updatedItem.name}: ${cartItem.quantity} → ${adjustedQuantity}`);
            }
            
            return {
              ...cartItem,
              product: { ...cartItem.product, quantity: updatedItem.quantity },
              quantity: adjustedQuantity
            };
          }
          return cartItem;
        }).filter(cartItem => cartItem.quantity > 0) // Remove items with 0 quantity
      );
    }
  }, []);

  // Set up real-time subscription for the sell modal
  React.useEffect(() => {
    if (!isOpen || !selectedBusiness?.id) return;

    console.log('🔌 SellModal: Setting up real-time subscription for stock items...');
    
    const channel = supabase
      .channel('sell_modal_stock_changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'stock_items',
          filter: `business_id=eq.${selectedBusiness.id}`
        },
        handleStockUpdate
      )
      .subscribe((status) => {
        console.log('📡 SellModal: Stock subscription status:', status);
      });

    return () => {
      console.log('🔌 SellModal: Cleaning up stock subscription...');
      supabase.removeChannel(channel);
    };
  }, [isOpen, selectedBusiness?.id, handleStockUpdate]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  // Filter products based on search and category
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
                           (product.sku && product.sku.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'all' || product.category?.name === selectedCategory;
      
      return matchesSearch && matchesCategory && !product.is_deleted && product.quantity > 0;
    });
  }, [products, searchQuery, selectedCategory]);

  // Get categories with item counts (only products with stock)
  const availableCategories = useMemo(() => {
    const categoryMap = new Map();
    
    products.forEach(product => {
      if (!product.is_deleted && product.quantity > 0 && product.category) {
        const categoryName = product.category.name;
        if (!categoryMap.has(categoryName)) {
          categoryMap.set(categoryName, {
            id: product.category.id,
            name: categoryName,
            count: 0
          });
        }
        categoryMap.get(categoryName).count++;
      }
    });
    
    return Array.from(categoryMap.values()).filter(cat => cat.count > 0);
  }, [products]);

  // Add product to cart
  const addToCart = (product: StockItem, quantity: number = 1) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product.id === product.id);
      
      if (existingItem) {
        // Update quantity if item already in cart
        const newQuantity = Math.min(existingItem.quantity + quantity, product.quantity);
        return prevCart.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: newQuantity }
            : item
        );
      } else {
        // Add new item to cart
        return [...prevCart, { product, quantity: Math.min(quantity, product.quantity) }];
      }
    });
  };

  // Update cart item quantity
  const updateCartQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCart(prevCart =>
      prevCart.map(item => {
        if (item.product.id === productId) {
          const maxQuantity = item.product.quantity;
          return { ...item, quantity: Math.min(quantity, maxQuantity) };
        }
        return item;
      })
    );
  };

  // Remove item from cart
  const removeFromCart = (productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.product.id !== productId));
  };

  // Add discount
  const addDiscount = () => {
    if (!discountForm.title.trim() || !discountForm.amount || parseFloat(discountForm.amount) <= 0) {
      return;
    }

    const amount = parseFloat(discountForm.amount);
    if (discountForm.type === 'percentage' && amount > 100) {
      return;
    }

    const newDiscount = {
      id: Date.now().toString(),
      title: discountForm.title.trim(),
      type: discountForm.type,
      amount: amount
    };

    setDiscounts(prev => [...prev, newDiscount]);
    setDiscountForm({ title: '', type: 'percentage', amount: '' });
    setShowDiscountForm(false);
  };

  // Remove discount
  const removeDiscount = (discountId: string) => {
    setDiscounts(prev => prev.filter(d => d.id !== discountId));
  };

  // Validate discount code
  const validateDiscountCode = async (code: string) => {
    if (!selectedBusiness?.id || !code.trim()) {
      setCodeValidation(null);
      return;
    }

    setValidatingCode(true);

    try {
      const validation = await DiscountCodeService.validateDiscountCode(
        selectedBusiness.id,
        code,
        cartTotals.subtotal
      );
      setCodeValidation(validation);
    } catch (error) {
      console.error('Error validating discount code:', error);
      setCodeValidation({
        isValid: false,
        error: 'Failed to validate discount code'
      });
    } finally {
      setValidatingCode(false);
    }
  };

  // Handle discount code change with debouncing
  const handleDiscountCodeChange = (code: string) => {
    setDiscountCode(code);
    
    // Debounce validation
    const timeoutId = setTimeout(() => {
      validateDiscountCode(code);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  // Apply discount code
  const applyDiscountCode = () => {
    if (!codeValidation?.isValid || !codeValidation.code) return;

    const newDiscount = {
      id: Date.now().toString(),
      title: codeValidation.code.description || codeValidation.code.code,
      type: codeValidation.code.type,
      amount: codeValidation.code.value
    };

    setDiscounts(prev => [...prev, newDiscount]);
    setDiscountCode('');
    setCodeValidation(null);
    setShowDiscountCodeForm(false);
  };

  // Calculate cart totals with tax and discounts
  const cartTotals = useMemo(() => {
    const items = cart.map(item => ({
      price: item.product.price,
      quantity: item.quantity,
      tax_exempt: false // For now, assume no products are tax exempt
    }));
    
    const baseTotals = calculateProductSaleTax(items, 5); // 5% GST
    
    // Calculate total discount amount
    const totalDiscounts = discounts.reduce((sum, discount) => {
      const discountAmount = discount.type === 'percentage' 
        ? (baseTotals.subtotal * discount.amount / 100)
        : discount.amount;
      return sum + discountAmount;
    }, 0);
    
    // Apply discounts to subtotal, then recalculate tax
    const subtotalAfterDiscounts = Math.max(0, baseTotals.subtotal - totalDiscounts);
    const taxAfterDiscounts = Math.round((subtotalAfterDiscounts * 0.05) * 100) / 100;
    
    return {
      ...baseTotals,
      subtotal_after_discounts: subtotalAfterDiscounts,
      tax_amount: taxAfterDiscounts,
      total_discounts: totalDiscounts,
      total: subtotalAfterDiscounts + taxAfterDiscounts,
      original_subtotal: baseTotals.subtotal
    };
  }, [cart, discounts]);

  // Keep cartTotal for backward compatibility
  const cartTotal = cartTotals.total;

  // Get cart item count
  const cartItemCount = useMemo(() => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  }, [cart]);

  // Check if product is in cart
  const getCartQuantity = (productId: string) => {
    const cartItem = cart.find(item => item.product.id === productId);
    return cartItem ? cartItem.quantity : 0;
  };

  // Handle payment options
  const handleTakePayment = () => {
    console.log('🎯 Take Payment clicked!');
    console.log('Current cart:', cart);
    console.log('Current total:', cartTotal);
    console.log('Selected business:', selectedBusiness?.id);
    
    // Close full-screen cart and open payment modal
    setIsFullScreenCart(false);
    setShowPaymentModal(true);
    console.log('Payment modal should now be open:', true);
  };

  const handleSendInvoice = () => {
    if (!selectedCustomer) {
      alert('Please add a customer before sending an invoice.');
      return;
    }
    
    if (cart.length === 0) {
      alert('Please add items to cart before sending an invoice.');
      return;
    }
    
    console.log('🔍 Opening SendInvoiceModal for cart invoice...');
    // Close full-screen cart and open invoice modal
    setIsFullScreenCart(false);
    setShowSendInvoiceModal(true);
  };

  const handleSendInvoiceMethod = async (method: 'email' | 'sms', allowStripePay: boolean) => {
    if (method === 'email') {
      await handleSendInvoiceEmail(allowStripePay);
    } else {
      // TODO: Implement SMS sending
      alert('SMS sending not yet implemented for product invoices');
    }
  };

  // Generate invoice PDF from StyledInvoiceDisplay
  const generateProductInvoicePDF = async (productSale: any): Promise<{ blob: Blob; filename: string } | null> => {
    try {
      // Import libraries dynamically
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf')
      ]);
      
      // Create a temporary invoice display element
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.width = '800px';
      tempContainer.style.backgroundColor = '#ffffff';
      tempContainer.style.padding = '20px';
      
      // Use the productSale data which includes the correct discounted totals
      const invoiceTotals = {
        subtotal: productSale.original_subtotal || productSale.subtotal_amount,
        subtotal_after_discounts: productSale.subtotal_amount,
        tax_amount: productSale.tax_amount,
        tax_rate: productSale.tax_rate,
        total_discounts: productSale.discount_amount || 0,
        total: productSale.total_amount
      };

      // Create sale data for StyledInvoiceDisplay (matching ProductSaleSuccessModal format)
      const saleForInvoice = {
        saleNumber: productSale.sale_number,
        saleData: {
          subtotal: invoiceTotals.subtotal,
          subtotal_after_discounts: invoiceTotals.subtotal_after_discounts,
          taxAmount: invoiceTotals.tax_amount,
          total: invoiceTotals.total,
          total_discounts: invoiceTotals.total_discounts,
          discounts: productSale.discount_details || [],
          items: cart.map(item => ({
            name: item.product.name,
            quantity: item.quantity,
            price: item.product.price
          }))
        },
        paymentMethod: 'pending',
        saleDate: productSale.created_at
      };

      // Create a React root to render the invoice
      const { createRoot } = await import('react-dom/client');
      const root = createRoot(tempContainer);
      
      // Import StyledInvoiceDisplay
      const { default: StyledInvoiceDisplay } = await import('./StyledInvoiceDisplay');
      
      // Render the invoice
      await new Promise<void>((resolve) => {
        root.render(
          React.createElement(StyledInvoiceDisplay, {
            sale: saleForInvoice,
            business: selectedBusiness,
            customer: selectedCustomer,
            className: 'invoice-preview'
          })
        );
        
        // Wait for render
        setTimeout(resolve, 100);
      });

      document.body.appendChild(tempContainer);

      // Wait for fonts and images to load
      await new Promise(resolve => setTimeout(resolve, 500));

      // Convert to canvas
      const canvas = await html2canvas(tempContainer, {
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 800,
        height: tempContainer.scrollHeight
      });
      
      // Clean up
      document.body.removeChild(tempContainer);
      root.unmount();
      
      // Create PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Calculate dimensions to fit A4
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      // Add first page
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Add additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Generate filename
      const invoiceNumber = `INV-${Date.now()}`;
      const filename = `invoice-${invoiceNumber}.pdf`;
      
      // Get PDF as blob
      const blob = pdf.output('blob');
      
      return { blob, filename };
    } catch (error) {
      console.error('Error generating product invoice PDF:', error);
      alert('Failed to generate PDF. Please try again.');
      return null;
    }
  };

  const handleSendInvoiceEmail = async (allowStripePay: boolean = true) => {
    console.log('🚀 Starting invoice email process...');
    console.log('Selected customer:', selectedCustomer);
    console.log('Selected business:', selectedBusiness);
    console.log('Cart items:', cart);
    
    if (!selectedCustomer?.email || !selectedBusiness) {
      const errorMsg = !selectedCustomer ? 'No customer selected' : 
                      !selectedCustomer.email ? 'Customer has no email address' : 
                      'No business selected';
      alert(errorMsg);
      return;
    }

    if (cart.length === 0) {
      alert('No items in cart to invoice');
      return;
    }

    try {
      setIsSendingInvoice(true);

      // Use the already calculated cartTotals (which includes discounts)
      // Don't recalculate here as it would lose the discount information

      // Generate a temporary invoice number for the pending invoice
      const invoiceNumber = `INV-${Date.now()}`;

      console.log('📧 Creating professional invoice email for cart items...');

      // Create the product sale using the proper function (this will create stock transactions)
      console.log('💾 Creating product sale record...');
      
      const productSale = await createProductSale({
        business_id: selectedBusiness.id,
        customer_id: selectedCustomer.id,
        subtotal_amount: cartTotals.subtotal_after_discounts || cartTotals.subtotal,
        tax_rate: cartTotals.tax_rate,
        tax_amount: cartTotals.tax_amount,
        total_amount: cartTotals.total,
        discount_amount: cartTotals.total_discounts || 0,
        discount_details: discounts,
        original_subtotal: cartTotals.subtotal,
        payment_status: 'unpaid', // Will be updated when payment is received
        notes: `Invoice ${invoiceNumber} - Products sale`,
        items: cart.map(item => ({
          stock_item_id: item.product.id,
          product_name: item.product.name,
          quantity: item.quantity,
          unit_price: item.product.price,
          tax_exempt: false, // Default for now
          tax_rate: cartTotals.tax_rate,
          tax_amount: (item.product.price * item.quantity * cartTotals.tax_rate) / 100,
          total_price: item.product.price * item.quantity
        }))
      });

      console.log('✅ Product sale record created with stock transactions:', productSale.id, 'Sale number:', productSale.sale_number);

      // Now generate PDF with the actual sale data
      const pdfData = await generateProductInvoicePDF(productSale);
      if (!pdfData) {
        console.error('Failed to generate PDF');
        return;
      }

      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const base64String = reader.result as string;
          // Remove data:application/pdf;base64, prefix
          const base64Content = base64String.split(',')[1];
          resolve(base64Content);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(pdfData.blob);
      
      const base64Content = await base64Promise;

      console.log('PDF attachment prepared:', {
        filename: pdfData.filename,
        contentLength: base64Content.length,
        contentPreview: base64Content.substring(0, 50) + '...'
      });


      // Generate payment link if Stripe Connect is enabled and allowStripePay is true
      let paymentLinkHtml = '';

      if (allowStripePay && selectedBusiness.stripe_connect_account_id && selectedBusiness.online_payments_enabled) {
        console.log('🔍 Creating Stripe payment link for product sale...');
        
        try {
          // Create payment record linked to the product sale
          const { data: pendingPayment, error: paymentError } = await supabase
            .from('payments')
            .insert({
              business_id: selectedBusiness.id,
              booking_id: null, // NULL for product sales
              product_sale_id: productSale.id, // ✅ DIRECT FOREIGN KEY LINK
              amount: cartTotals.total,
              method: 'credit_card',
              status: 'pending',
              reference: `Product Sale ${productSale.sale_number}`,
              notes: `Payment for product sale ${productSale.sale_number} - Product invoice`,
              payment_token: crypto.randomUUID()
            })
            .select()
            .single();

          if (paymentError) {
            console.error('Failed to create payment record:', paymentError);
            console.log('⚠️ Continuing without payment link due to database constraint');
          } else {
            console.log('✅ Pending payment record created:', pendingPayment.id);

            // Generate payment link
            const { data: linkData, error: linkError } = await supabase.functions.invoke('generate-invoice-payment-link', {
              body: {
                paymentId: pendingPayment.id
              }
            });

            if (!linkError && linkData?.paymentUrl) {
              paymentLinkHtml = `
                <div style="margin: 30px 0; padding: 25px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 12px; text-align: center;">
                  <h3 style="color: white; margin: 0 0 15px 0; font-size: 18px;">Pay Your Invoice Online</h3>
                  <p style="color: #e2e8f0; margin: 0 0 20px 0; font-size: 14px;">Secure payment with credit or debit card</p>
                  <a href="${linkData.paymentUrl}" 
                     style="display: inline-block; background: #ffffff; color: #667eea; padding: 12px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                    Pay $${cartTotals.total.toFixed(2)} Now
                  </a>
                  <p style="color: #cbd5e0; margin: 15px 0 0 0; font-size: 12px;">Powered by Stripe - Secure &amp; Encrypted</p>
                </div>
              `;
              console.log('✅ Payment link generated:', linkData.paymentUrl);
            } else {
              console.error('Failed to generate payment link:', linkError);
            }
          }
        } catch (paymentLinkError) {
          console.error('Error in payment link generation:', paymentLinkError);
          console.log('⚠️ Continuing without payment link');
        }
      }

      // Prepare email content using the same professional format as booking invoices
      const customerName = `${selectedCustomer.first_name} ${selectedCustomer.last_name}`;
      const actualInvoiceNumber = productSale.sale_number || invoiceNumber;
      const emailSubject = `Invoice #${actualInvoiceNumber} from ${selectedBusiness.name}`;

      const emailBody = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
          <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 30px; text-align: center; border-radius: 12px 12px 0 0;">
            <h1 style="color: white; margin: 0; font-size: 28px;">Invoice #${actualInvoiceNumber}</h1>
            <p style="color: #e2e8f0; margin: 10px 0 0 0; font-size: 16px;">from ${selectedBusiness.name}</p>
          </div>
          
          <div style="padding: 30px;">
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">Hi ${customerName},</p>
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">Please find attached your invoice for the following products:</p>
            
            ${paymentLinkHtml}
            
            <div style="background: #f8fafc; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #667eea;">
              <h4 style="color: #374151; margin: 0 0 10px 0; font-size: 16px;">Invoice Summary</h4>
              <div style="display: flex; justify-content: space-between; margin: 8px 0;">
                <span style="color: #6b7280;">Subtotal:</span>
                <span style="color: #374151; font-weight: bold;">$${cartTotals.subtotal.toFixed(2)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin: 8px 0;">
                <span style="color: #6b7280;">GST (5%):</span>
                <span style="color: #374151; font-weight: bold;">$${cartTotals.tax_amount.toFixed(2)}</span>
              </div>
              <div style="display: flex; justify-content: space-between; margin: 8px 0; padding-top: 8px; border-top: 1px solid #e5e7eb;">
                <span style="color: #374151; font-weight: bold;">Total Amount:</span>
                <span style="color: #059669; font-weight: bold; font-size: 18px;">$${cartTotals.total.toFixed(2)}</span>
              </div>
            </div>
            
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 20px 0;">Thank you for your business!</p>
            
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
              <p style="color: #374151; font-size: 16px; margin: 0;">Best regards,<br><strong>${selectedBusiness.name}</strong></p>
              ${selectedBusiness.phone ? `<p style="color: #6b7280; font-size: 14px; margin: 5px 0 0 0;">Phone: ${selectedBusiness.phone}</p>` : ''}
              ${selectedBusiness.email ? `<p style="color: #6b7280; font-size: 14px; margin: 5px 0 0 0;">Email: ${selectedBusiness.email}</p>` : ''}
            </div>
          </div>
          
          <div style="background: #f8fafc; padding: 20px; text-align: center; border-radius: 0 0 12px 12px; border-top: 1px solid #e5e7eb;">
            <p style="color: #6b7280; font-size: 14px; margin: 0;">This is an automated invoice from ${selectedBusiness.name}</p>
          </div>
        </div>
      `;

      // Send email with PDF attachment using Gmail integration
      const emailResult = await sendGmailEmail({
        businessId: selectedBusiness.id,
        to: selectedCustomer.email,
        subject: emailSubject,
        body: emailBody,
        attachments: [{
          filename: pdfData.filename,
          content: base64Content,
          type: 'application/pdf'
        }]
      });

      if (emailResult.success) {
        console.log('✅ Professional product invoice email sent successfully');

        // Show success message
        if (window.confirm('Invoice sent successfully with PDF attachment! Would you like to clear the cart?')) {
          setCart([]);
          setSelectedCustomer(null);
        }

        // Close the send invoice modal
        setShowSendInvoiceModal(false);

        // Refresh the modal to update stock quantities
        console.log('🔄 SellModal: Refreshing products after invoice sent...');
        onSuccess(); // This will trigger the parent's refresh
      } else {
        throw new Error(emailResult.error || 'Failed to send email');
      }

    } catch (error) {
      console.error('❌ Failed to send invoice email:', error);
      
      // More detailed error logging
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        alert(`Failed to send invoice: ${error.message}`);
      } else {
        console.error('Unknown error:', error);
        alert('Failed to send invoice. Please check the console for details.');
      }
    } finally {
      setIsSendingInvoice(false);
    }
  };

  const handlePaymentSuccess = () => {
    console.log('🎉 Payment successful! Refreshing stock data...');
    
    // Reset cart and close modals
    setCart([]);
    setShowCart(false);
    setCartState('cart');
    setShowPaymentModal(false);
    
    // Call parent success callback to refresh data
    if (onSuccess) {
      console.log('📊 Calling onSuccess callback to refresh stock data');
      onSuccess();
    } else {
      console.warn('⚠️ No onSuccess callback provided');
    }
    
    // Close the sell products modal
    onClose();
  };

  // Render product card
  const renderProductCard = (product: StockItem) => {
    const hasPhoto = productPhotos[product.id];
    const cartQuantity = getCartQuantity(product.id);
    const isInCart = cartQuantity > 0;
    
    return (
      <div
        key={product.id}
        className={`backdrop-blur-xl bg-white/70 border rounded-2xl overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02] ${
          isInCart ? 'ring-2 ring-green-500 border-green-200' : 'border-white/20'
        }`}
        style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
      >
        {/* Product Header */}
        <div className="p-3 sm:p-4 relative">
          {/* Cart Badge */}
          {isInCart && (
            <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-green-500 text-white rounded-lg shadow-lg z-10">
              <ShoppingCart className="h-3 w-3" />
              <span className="text-xs font-medium">{cartQuantity}</span>
            </div>
          )}

          {/* Product Photo */}
          <div className="flex justify-center mb-2">
            <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
              {hasPhoto ? (
                <img 
                  src={hasPhoto} 
                  alt={product.name}
                  className="w-full h-full object-contain"
                  style={{ imageRendering: 'crisp-edges' }}
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package2 className="h-6 w-6 sm:h-7 sm:w-7 text-gray-400" />
                </div>
              )}
            </div>
          </div>
          
          <div className="mb-2">
            <div className="flex items-start gap-2 mb-1">
              <div className="text-center flex-shrink-0">
                <div className="text-sm sm:text-lg font-bold text-gray-900">
                  {product.quantity}
                </div>
                <div className="text-[8px] sm:text-[9px] text-gray-500">{product.unit}</div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xs sm:text-sm font-semibold text-gray-900 leading-tight line-clamp-2">
                  {product.name}
                </h3>
                <div className="text-sm sm:text-base font-bold text-green-600 mt-1">
                  ${product.price.toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          {/* Category Tag */}
          {product.category?.name && (
            <div className="mb-3 flex justify-start">
              <div className="flex items-center gap-1 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-purple-100/50 rounded-md sm:rounded-lg">
                <Tag className="h-2.5 w-2.5 sm:h-3 sm:w-3 text-purple-600" />
                <span className="text-[10px] sm:text-xs text-purple-700 font-medium truncate max-w-[80px] sm:max-w-none">
                  {product.category.name}
                </span>
              </div>
            </div>
          )}

          {/* Add to Cart Controls */}
          {isInCart ? (
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={() => updateCartQuantity(product.id, cartQuantity - 1)}
                className="flex items-center justify-center w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors duration-200"
              >
                <Minus className="h-4 w-4" />
              </button>
              
              <div className="flex-1 text-center">
                <div className="text-sm font-bold text-gray-900">{cartQuantity} in cart</div>
              </div>
              
              <button
                onClick={() => updateCartQuantity(product.id, cartQuantity + 1)}
                disabled={cartQuantity >= product.quantity}
                className="flex items-center justify-center w-8 h-8 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white rounded-lg transition-colors duration-200"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => addToCart(product, 1)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 font-medium text-sm"
            >
              <Plus className="h-4 w-4" />
              Add to Cart
            </button>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen && !isAnimating) return null;

  const modalContent = (
    <div
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100020] flex items-center justify-center`}
      onClick={onClose}
      style={{
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        isolation: 'isolate',
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <div
        className={`w-full h-full backdrop-blur-xl bg-white/90 transform transition-all duration-300 ease-out ${
          isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        style={{
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)'
        }}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="relative bg-gradient-to-r from-green-600 to-emerald-600 border-b border-green-500/30 shadow-lg">
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-2xl bg-white/20 flex items-center justify-center shadow-sm">
                  <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-white">
                    Sell Products
                  </h2>
                  <div className="text-sm text-green-100">
                    Select products to sell
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                {/* Cart Button */}
                {cart.length > 0 && (
                  <button
                    onClick={() => setShowCart(!showCart)}
                    className="relative flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/20 hover:bg-white/30 text-white transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 touch-manipulation shadow-sm"
                  >
                    <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6" />
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-400 text-green-800 rounded-full flex items-center justify-center text-xs font-bold">
                      {cartItemCount}
                    </div>
                  </button>
                )}
                
                <button
                  onClick={onClose}
                  className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/20 hover:bg-white/30 text-white transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 touch-manipulation shadow-sm"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden flex">
            {/* Products Section */}
            <div className={`transition-all duration-300 ${showCart ? 'w-2/3' : 'w-full'} flex flex-col`}>
              {/* Search and Categories */}
              <div className="border-b border-gray-200/50 p-4 sm:p-6 bg-white/50">
                {/* Search Bar */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500/50 transition-all duration-300 ease-out shadow-sm text-sm"
                      style={{ 
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)'
                      }}
                    />
                  </div>
                </div>

                {/* Category Tabs */}
                <div className="relative">
                  <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                        selectedCategory === 'all'
                          ? 'bg-green-500 text-white shadow-lg'
                          : 'bg-white/50 text-gray-700 hover:bg-white/70 border border-white/30'
                      }`}
                    >
                      All Products ({filteredProducts.length})
                    </button>
                    {availableCategories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.name)}
                        className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                          selectedCategory === category.name
                            ? 'bg-green-500 text-white shadow-lg'
                            : 'bg-white/50 text-gray-700 hover:bg-white/70 border border-white/30'
                        }`}
                      >
                        {category.name} ({category.count})
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Products Grid */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
                {filteredProducts.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
                    {filteredProducts.map(renderProductCard)}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <Package2 className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No products found</h3>
                    <p className="text-gray-500">
                      {searchQuery ? `No products match "${searchQuery}"` : 'No products available in this category'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Cart Sidebar */}
            {showCart && (
              <div className="w-1/3 border-l border-gray-200/50 bg-white/60 backdrop-blur-sm flex flex-col">
                {cartState === 'cart' ? (
                  <>
                    {/* Cart Header */}
                    <div className="p-4 border-b border-gray-200/50">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                        <ShoppingCart className="h-5 w-5" />
                        Cart ({cartItemCount} items)
                      </h3>
                    </div>
                    
                    {/* Cart Items */}
                    <div className="flex-1 overflow-y-auto p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
                      {cart.length > 0 ? (
                        <div className="space-y-3">
                          {cart.map((item) => (
                            <div key={item.product.id} className="bg-white/80 rounded-xl p-3 shadow-sm">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm font-semibold text-gray-900 truncate">
                                    {item.product.name}
                                  </h4>
                                  <div className="text-xs text-gray-500">
                                    ${item.product.price.toFixed(2)} each
                                  </div>
                                </div>
                                <button
                                  onClick={() => removeFromCart(item.product.id)}
                                  className="text-red-500 hover:text-red-700 p-1"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                              
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => updateCartQuantity(item.product.id, item.quantity - 1)}
                                    className="w-6 h-6 bg-gray-200 hover:bg-gray-300 rounded flex items-center justify-center"
                                  >
                                    <Minus className="h-3 w-3" />
                                  </button>
                                  <span className="text-sm font-medium w-8 text-center">
                                    {item.quantity}
                                  </span>
                                  <button
                                    onClick={() => updateCartQuantity(item.product.id, item.quantity + 1)}
                                    disabled={item.quantity >= item.product.quantity}
                                    className="w-6 h-6 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 rounded flex items-center justify-center"
                                  >
                                    <Plus className="h-3 w-3" />
                                  </button>
                                </div>
                                <div className="text-sm font-bold text-gray-900">
                                  ${(item.product.price * item.quantity).toFixed(2)}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center text-gray-500 mt-8">
                          <ShoppingCart className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                          <p>Your cart is empty</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Cart Footer */}
                    {cart.length > 0 && (
                      <div className="p-4 border-t border-gray-200/50 bg-white/80">
                        <div className="mb-4 space-y-2">
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">Subtotal:</span>
                            <span className="font-medium">${cartTotals.subtotal.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">GST ({cartTotals.tax_rate}%):</span>
                            <span className="font-medium">${cartTotals.tax_amount.toFixed(2)}</span>
                          </div>
                          <div className="border-t border-gray-200/50 pt-2 flex justify-between items-center text-lg font-bold">
                            <span>Total:</span>
                            <span className="text-green-600">${cartTotal.toFixed(2)}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => setCartState('payment')}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 font-bold text-base shadow-lg"
                        >
                          <CreditCard className="h-5 w-5" />
                          Pay ${cartTotal.toFixed(2)}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Payment Header */}
                    <div className="relative bg-gradient-to-r from-green-600 to-emerald-600 border-b border-green-700">
                      <div className="flex items-center justify-between px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                            <CreditCard className="h-4 w-4 text-white" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-white">Complete Sale</h3>
                            <div className="text-sm text-green-100">Choose payment method</div>
                          </div>
                        </div>
                        <button
                          onClick={() => setCartState('cart')}
                          className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-700/50 hover:bg-green-700 text-white transition-all duration-300"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Payment Content */}
                    <div className="flex-1 overflow-y-auto p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
                      <div className="space-y-4">
                        {/* Sale Summary */}
                        <div className="bg-white/80 rounded-xl p-4 shadow-sm border border-gray-200/50">
                          <h4 className="text-sm font-bold text-gray-800 mb-3">Sale Summary</h4>
                          <ul className="space-y-2 mb-3">
                            {cart.map((item, index) => (
                              <li key={index} className="flex justify-between items-center text-sm text-gray-700">
                                <span>{item.product.name} ×{item.quantity}</span>
                                <span>${(item.product.price * item.quantity).toFixed(2)}</span>
                              </li>
                            ))}
                          </ul>
                          <div className="border-t border-gray-200/50 pt-3 space-y-2">
                            <div className="flex justify-between items-center text-sm text-gray-600">
                              <span>Subtotal</span>
                              <span>${cartTotals.subtotal.toFixed(2)}</span>
                            </div>
                            {discounts.map((discount) => (
                              <div key={discount.id} className="flex justify-between items-center text-sm text-red-600">
                                <span>
                                  {discount.title} ({discount.type === 'percentage' ? `${discount.amount}%` : `$${discount.amount}`})
                                </span>
                                <span>-${(discount.type === 'percentage' 
                                  ? (cartTotals.subtotal * discount.amount / 100)
                                  : discount.amount).toFixed(2)}</span>
                              </div>
                            ))}
                            <div className="flex justify-between items-center text-sm text-gray-600">
                              <span>GST ({cartTotals.tax_rate}%)</span>
                              <span>${cartTotals.tax_amount.toFixed(2)}</span>
                            </div>
                            <div className="border-t border-gray-200/50 pt-2 flex justify-between items-center font-bold text-gray-900">
                              <span>Total</span>
                              <span className="text-green-600">${cartTotal.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>

                        {/* Discounts Section */}
                        <div className="bg-white/80 rounded-xl p-4 shadow-sm border border-gray-200/50">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-bold text-gray-800">Discounts</h4>
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setShowDiscountCodeForm(!showDiscountCodeForm);
                                  setShowDiscountForm(false);
                                }}
                                className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                              >
                                {showDiscountCodeForm ? 'Cancel' : 'Enter Discount Code'}
                              </button>
                              <button
                                onClick={() => {
                                  setShowDiscountForm(!showDiscountForm);
                                  setShowDiscountCodeForm(false);
                                }}
                                className="text-xs font-medium text-purple-600 hover:text-purple-700 transition-colors"
                              >
                                {showDiscountForm ? 'Cancel' : 'Add Custom Discount'}
                              </button>
                            </div>
                          </div>

                          {/* Discount Code Form */}
                          {showDiscountCodeForm && (
                            <div className="space-y-3 mb-4 p-3 bg-blue-50/80 rounded-lg border border-blue-200/50">
                              <div>
                                <label className="block text-xs font-medium text-blue-800 mb-2">
                                  Discount Code
                                </label>
                                <div className="relative">
                                  <input
                                    type="text"
                                    placeholder="Enter discount code"
                                    value={discountCode}
                                    onChange={(e) => handleDiscountCodeChange(e.target.value.toUpperCase())}
                                    className="w-full px-3 py-2 text-sm border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono uppercase"
                                  />
                                  {validatingCode && (
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                      <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                                    </div>
                                  )}
                                  {codeValidation?.isValid && (
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                      <Check className="h-4 w-4 text-green-600" />
                                    </div>
                                  )}
                                </div>
                                
                                {/* Code Validation Feedback */}
                                {codeValidation && !validatingCode && (
                                  <div className={`mt-2 text-xs ${codeValidation.isValid ? 'text-green-600' : 'text-red-600'}`}>
                                    {codeValidation.isValid ? (
                                      <div className="space-y-1">
                                        <div className="flex items-center">
                                          <Check className="h-3 w-3 mr-1" />
                                          Valid discount code
                                        </div>
                                        {codeValidation.code?.description && (
                                          <div className="text-gray-600 text-xs">
                                            {codeValidation.code.description}
                                          </div>
                                        )}
                                        <div className="text-xs">
                                          Discount: {codeValidation.code?.type === 'percentage' 
                                            ? `${codeValidation.code.value}%` 
                                            : `$${codeValidation.code?.value}`}
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex items-center">
                                        <X className="h-3 w-3 mr-1" />
                                        {codeValidation.error}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              <button
                                onClick={applyDiscountCode}
                                disabled={!codeValidation?.isValid || !discountCode.trim()}
                                className="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                Apply Discount Code
                              </button>
                            </div>
                          )}

                          {/* Custom Discount Form */}
                          {showDiscountForm && (
                            <div className="space-y-3 mb-4 p-3 bg-purple-50/80 rounded-lg border border-purple-200/50">
                              <div>
                                <label className="block text-xs font-medium text-purple-800 mb-2">
                                  Discount Name
                                </label>
                                <input
                                  type="text"
                                  placeholder="Discount name"
                                  value={discountForm.title}
                                  onChange={(e) => setDiscountForm(prev => ({ ...prev, title: e.target.value }))}
                                  className="w-full px-3 py-2 text-sm border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                />
                              </div>
                              
                              <div className="flex gap-2">
                                <select
                                  value={discountForm.type}
                                  onChange={(e) => setDiscountForm(prev => ({ ...prev, type: e.target.value as 'percentage' | 'fixed' }))}
                                  className="flex-1 px-3 py-2 text-sm border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                >
                                  <option value="percentage">Percentage</option>
                                  <option value="fixed">Fixed Amount</option>
                                </select>
                                
                                <div className="relative flex-1">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <span className="text-gray-500 text-sm">
                                      {discountForm.type === 'percentage' ? '%' : '$'}
                                    </span>
                                  </div>
                                  <input
                                    type="number"
                                    min="0"
                                    step={discountForm.type === 'percentage' ? '1' : '0.01'}
                                    max={discountForm.type === 'percentage' ? "100" : undefined}
                                    placeholder={discountForm.type === 'percentage' ? '10' : '5.00'}
                                    value={discountForm.amount}
                                    onChange={(e) => setDiscountForm(prev => ({ ...prev, amount: e.target.value }))}
                                    className="w-full pl-7 pr-3 py-2 text-sm border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                  />
                                </div>
                              </div>
                              
                              <button
                                onClick={addDiscount}
                                disabled={!discountForm.title.trim() || !discountForm.amount || parseFloat(discountForm.amount) <= 0}
                                className="w-full px-3 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                Add Custom Discount
                              </button>
                            </div>
                          )}

                          {/* Applied Discounts */}
                          {discounts.length > 0 ? (
                            <div className="space-y-2">
                              {discounts.map((discount) => (
                                <div key={discount.id} className="flex items-center justify-between p-2 bg-red-50/80 border border-red-200/50 rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <Percent className="h-3 w-3 text-red-600" />
                                    <span className="text-sm font-medium text-red-800">
                                      {discount.title}
                                    </span>
                                    <span className="text-xs text-red-600">
                                      ({discount.type === 'percentage' ? `${discount.amount}%` : `$${discount.amount}`})
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => removeDiscount(discount.id)}
                                    className="text-red-400 hover:text-red-600 p-1 transition-colors"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-2">
                              <p className="text-xs text-gray-500">No discounts applied</p>
                            </div>
                          )}
                        </div>

                        {/* Customer Selection (Optional) */}
                        <div className="bg-white/80 rounded-xl p-4 shadow-sm border border-gray-200/50">
                          <h4 className="text-sm font-bold text-gray-800 mb-3">Customer (Optional)</h4>
                          
                          {selectedCustomer ? (
                            <div className="flex items-center justify-between p-3 bg-green-50/80 border border-green-200/50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
                                  <User className="h-4 w-4 text-green-600" />
                                </div>
                                <div>
                                  <div className="font-bold text-gray-900 text-sm">
                                    {selectedCustomer.first_name} {selectedCustomer.last_name}
                                  </div>
                                  <div className="text-xs text-gray-600">
                                    {selectedCustomer.email || selectedCustomer.phone || 'No contact info'}
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => setSelectedCustomer(null)}
                                className="text-gray-500 hover:text-red-500 p-1 transition-colors"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setIsSelectCustomerModalOpen(true)}
                              className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300/50 rounded-lg hover:border-green-300/50 hover:bg-green-50/40 transition-all duration-200 text-gray-600 hover:text-green-600"
                            >
                              <UserPlus className="h-4 w-4" />
                              <span className="font-medium text-sm">Add Customer</span>
                            </button>
                          )}
                        </div>

                        {/* Payment Options */}
                        <div className="space-y-3">
                          <h4 className="text-sm font-bold text-gray-800">Payment Options</h4>
                          
                          <button
                            onClick={handleTakePayment}
                            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center">
                                <CreditCard className="h-5 w-5" />
                              </div>
                              <div className="text-left">
                                <div className="font-bold">Take Payment</div>
                                <div className="text-sm text-green-100">Process payment now</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold">${cartTotal.toFixed(2)}</div>
                              <div className="text-xs text-green-100">Immediate</div>
                            </div>
                          </button>

                          <button
                            onClick={handleSendInvoice}
                            className="w-full flex items-center justify-between p-4 bg-white/80 border border-gray-300/50 text-gray-700 rounded-xl hover:bg-gray-50/80 transition-all duration-200 shadow-md"
                          >
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                                <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                              </div>
                              <div className="text-left">
                                <div className="font-bold text-gray-900">Send Invoice</div>
                                <div className="text-sm text-gray-600">Email invoice to customer</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-gray-900">${cartTotal.toFixed(2)}</div>
                              <div className="text-xs text-gray-500">Pay later</div>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Payment Footer */}
                    <div className="p-4 border-t border-gray-200/50 bg-white/80">
                      <button
                        onClick={() => setCartState('cart')}
                        className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200 font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Fixed Pay Button (when cart is hidden) */}
          {!showCart && cart.length > 0 && (
            <div className="absolute bottom-4 right-4">
              <button
                onClick={() => {
                  // On mobile, open full-screen cart. On desktop, show side cart.
                  const isMobile = window.innerWidth < 768;
                  if (isMobile) {
                    setIsFullScreenCart(true);
                  } else {
                    setShowCart(true);
                  }
                }}
                className="flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-2xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 font-bold shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <ShoppingCart className="h-5 w-5" />
                <span>View Cart ({cartItemCount})</span>
                <div className="bg-white/20 px-2 py-1 rounded-lg text-sm">
                  ${cartTotal.toFixed(2)}
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Full-Screen Cart (Mobile) */}
      {isFullScreenCart && (
        <div 
          className="fixed inset-0 bg-white z-[60] flex flex-col"
          onClick={(e) => {
            // Prevent closing when clicking inside the cart
            e.stopPropagation();
            console.log('Full-screen cart clicked - preventing close');
          }}
        >
          {/* Full-Screen Cart Header */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-4 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <ShoppingCart className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Cart ({cartItemCount} items)</h3>
                  <div className="text-sm text-green-100">Select products to sell</div>
                </div>
              </div>
              <button
                onClick={() => setIsFullScreenCart(false)}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-700/50 hover:bg-green-700 text-white transition-all duration-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {cartState === 'cart' ? (
            <>
              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
                {cart.length > 0 ? (
                  <div className="space-y-3">
                    {cart.map((item) => (
                      <div key={item.product.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-200/50">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-base font-semibold text-gray-900">
                              {item.product.name}
                            </h4>
                            <div className="text-sm text-gray-500">
                              ${item.product.price.toFixed(2)} each
                            </div>
                          </div>
                          <button
                            onClick={() => removeFromCart(item.product.id)}
                            className="text-red-500 hover:text-red-700 p-2"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => updateCartQuantity(item.product.id, item.quantity - 1)}
                              className="w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-lg flex items-center justify-center"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="text-lg font-bold text-gray-900 min-w-[2rem] text-center">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => updateCartQuantity(item.product.id, item.quantity + 1)}
                              className="w-8 h-8 bg-green-500 hover:bg-green-600 text-white rounded-lg flex items-center justify-center"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">
                              ${(item.product.price * item.quantity).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <ShoppingCart className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Cart is empty</h3>
                    <p className="text-gray-500 mb-4">Add some products to get started</p>
                    <button
                      onClick={() => setIsFullScreenCart(false)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Continue Shopping
                    </button>
                  </div>
                )}
              </div>

              {/* Cart Summary & Actions */}
              {cart.length > 0 && (
                <div className="border-t border-gray-200/50 p-4 bg-white">
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between items-center text-base">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-semibold">${cartTotals.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-base">
                      <span className="text-gray-600">GST (5%):</span>
                      <span className="font-semibold">${cartTotals.tax_amount.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-gray-200/50 pt-3 flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-900">Total:</span>
                      <span className="text-xl font-bold text-green-600">${cartTotal.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => setCartState('payment')}
                    className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold text-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-lg"
                  >
                    Pay ${cartTotal.toFixed(2)}
                  </button>
                </div>
              )}
            </>
          ) : (
            // Payment state content - reuse the existing payment content
            <div className="flex-1 flex flex-col">
              {/* Payment Header */}
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-4 shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                      <CreditCard className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">Complete Sale</h3>
                      <div className="text-sm text-green-100">Choose payment method</div>
                    </div>
                  </div>
                  <button
                    onClick={() => setCartState('cart')}
                    className="flex items-center justify-center w-8 h-8 rounded-lg bg-green-700/50 hover:bg-green-700 text-white transition-all duration-300"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Payment Content - Reuse existing payment content */}
              <div className="flex-1 overflow-y-auto p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="space-y-4">
                  {/* Sale Summary */}
                  <div className="bg-white/80 rounded-xl p-4 shadow-sm border border-gray-200/50">
                    <h4 className="text-sm font-bold text-gray-800 mb-3">Sale Summary</h4>
                    <ul className="space-y-2 mb-3">
                      {cart.map((item, index) => (
                        <li key={index} className="flex justify-between items-center text-sm text-gray-700">
                          <span>{item.product.name} ×{item.quantity}</span>
                          <span>${(item.product.price * item.quantity).toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="border-t border-gray-200/50 pt-3 space-y-2">
                      <div className="flex justify-between items-center text-sm text-gray-600">
                        <span>Subtotal</span>
                        <span>${cartTotals.subtotal.toFixed(2)}</span>
                      </div>
                      {discounts.map((discount) => (
                        <div key={discount.id} className="flex justify-between items-center text-sm text-red-600">
                          <span>
                            {discount.title} ({discount.type === 'percentage' ? `${discount.amount}%` : `$${discount.amount}`})
                          </span>
                          <span>-${(discount.type === 'percentage' 
                            ? (cartTotals.subtotal * discount.amount / 100)
                            : discount.amount).toFixed(2)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between items-center text-sm text-gray-600">
                        <span>GST ({cartTotals.tax_rate}%)</span>
                        <span>${cartTotals.tax_amount.toFixed(2)}</span>
                      </div>
                      <div className="border-t border-gray-200/50 pt-2 flex justify-between items-center font-bold text-gray-900">
                        <span>Total</span>
                        <span className="text-green-600">${cartTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Discounts Section */}
                  <div className="bg-white/80 rounded-xl p-4 shadow-sm border border-gray-200/50">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-bold text-gray-800">Discounts</h4>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setShowDiscountCodeForm(!showDiscountCodeForm);
                            setShowDiscountForm(false);
                          }}
                          className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
                        >
                          {showDiscountCodeForm ? 'Cancel' : 'Enter Discount Code'}
                        </button>
                        <button
                          onClick={() => {
                            setShowDiscountForm(!showDiscountForm);
                            setShowDiscountCodeForm(false);
                          }}
                          className="text-xs font-medium text-purple-600 hover:text-purple-700 transition-colors"
                        >
                          {showDiscountForm ? 'Cancel' : 'Add Custom Discount'}
                        </button>
                      </div>
                    </div>

                    {/* Discount Code Form */}
                    {showDiscountCodeForm && (
                      <div className="space-y-3 mb-4 p-3 bg-blue-50/80 rounded-lg border border-blue-200/50">
                        <div>
                          <label className="block text-xs font-medium text-blue-800 mb-2">
                            Discount Code
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Enter discount code"
                              value={discountCode}
                              onChange={(e) => handleDiscountCodeChange(e.target.value.toUpperCase())}
                              className="w-full px-3 py-2 text-sm border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono uppercase"
                            />
                            {validatingCode && (
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin"></div>
                              </div>
                            )}
                            {codeValidation?.isValid && (
                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                <Check className="h-4 w-4 text-green-600" />
                              </div>
                            )}
                          </div>
                          
                          {/* Code Validation Feedback */}
                          {codeValidation && !validatingCode && (
                            <div className={`mt-2 text-xs ${codeValidation.isValid ? 'text-green-600' : 'text-red-600'}`}>
                              {codeValidation.isValid ? (
                                <div className="space-y-1">
                                  <div className="flex items-center">
                                    <Check className="h-3 w-3 mr-1" />
                                    Valid discount code
                                  </div>
                                  {codeValidation.code?.description && (
                                    <div className="text-gray-600 text-xs">
                                      {codeValidation.code.description}
                                    </div>
                                  )}
                                  <div className="text-xs">
                                    Discount: {codeValidation.code?.type === 'percentage' 
                                      ? `${codeValidation.code.value}%` 
                                      : `$${codeValidation.code?.value}`}
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center">
                                  <X className="h-3 w-3 mr-1" />
                                  {codeValidation.error}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <button
                          onClick={applyDiscountCode}
                          disabled={!codeValidation?.isValid || !discountCode.trim()}
                          className="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Apply Discount Code
                        </button>
                      </div>
                    )}

                    {/* Custom Discount Form */}
                    {showDiscountForm && (
                      <div className="space-y-3 mb-4 p-3 bg-purple-50/80 rounded-lg border border-purple-200/50">
                        <div>
                          <label className="block text-xs font-medium text-purple-800 mb-2">
                            Discount Name
                          </label>
                          <input
                            type="text"
                            placeholder="Discount name"
                            value={discountForm.title}
                            onChange={(e) => setDiscountForm(prev => ({ ...prev, title: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                        </div>
                        
                        <div className="flex gap-2">
                          <select
                            value={discountForm.type}
                            onChange={(e) => setDiscountForm(prev => ({ ...prev, type: e.target.value as 'percentage' | 'fixed' }))}
                            className="flex-1 px-3 py-2 text-sm border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          >
                            <option value="percentage">Percentage</option>
                            <option value="fixed">Fixed Amount</option>
                          </select>
                          
                          <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 text-sm">
                                {discountForm.type === 'percentage' ? '%' : '$'}
                              </span>
                            </div>
                            <input
                              type="number"
                              min="0"
                              step={discountForm.type === 'percentage' ? '1' : '0.01'}
                              max={discountForm.type === 'percentage' ? "100" : undefined}
                              placeholder={discountForm.type === 'percentage' ? '10' : '5.00'}
                              value={discountForm.amount}
                              onChange={(e) => setDiscountForm(prev => ({ ...prev, amount: e.target.value }))}
                              className="w-full pl-7 pr-3 py-2 text-sm border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                        
                        <button
                          onClick={addDiscount}
                          disabled={!discountForm.title.trim() || !discountForm.amount || parseFloat(discountForm.amount) <= 0}
                          className="w-full px-3 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Add Custom Discount
                        </button>
                      </div>
                    )}

                    {/* Applied Discounts */}
                    {discounts.length > 0 ? (
                      <div className="space-y-2">
                        {discounts.map((discount) => (
                          <div key={discount.id} className="flex items-center justify-between p-2 bg-red-50/80 border border-red-200/50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Percent className="h-3 w-3 text-red-600" />
                              <span className="text-sm font-medium text-red-800">
                                {discount.title}
                              </span>
                              <span className="text-xs text-red-600">
                                ({discount.type === 'percentage' ? `${discount.amount}%` : `$${discount.amount}`})
                              </span>
                            </div>
                            <button
                              onClick={() => removeDiscount(discount.id)}
                              className="text-red-400 hover:text-red-600 p-1 transition-colors"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-2">
                        <p className="text-xs text-gray-500">No discounts applied</p>
                      </div>
                    )}
                  </div>

                  {/* Customer Selection (Optional) */}
                  <div className="bg-white/80 rounded-xl p-4 shadow-sm border border-gray-200/50">
                    <h4 className="text-sm font-bold text-gray-800 mb-3">Customer (Optional)</h4>
                    
                    {selectedCustomer ? (
                      <div className="flex items-center justify-between p-3 bg-green-50/80 border border-green-200/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-lg bg-green-100 flex items-center justify-center">
                            <User className="h-4 w-4 text-green-600" />
                          </div>
                          <div>
                            <div className="font-bold text-gray-900 text-sm">
                              {selectedCustomer.first_name} {selectedCustomer.last_name}
                            </div>
                            <div className="text-xs text-gray-600">
                              {selectedCustomer.email || selectedCustomer.phone || 'No contact info'}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedCustomer(null)}
                          className="text-gray-500 hover:text-red-500 p-1 transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setIsSelectCustomerModalOpen(true)}
                        className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300/50 rounded-lg hover:border-green-300/50 hover:bg-green-50/40 transition-all duration-200 text-gray-600 hover:text-green-600"
                      >
                        <UserPlus className="h-4 w-4" />
                        <span className="font-medium text-sm">Add Customer</span>
                      </button>
                    )}
                  </div>

                  {/* Payment Options */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-bold text-gray-800">Payment Options</h4>
                    
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('🔥 Take Payment button clicked in full-screen cart');
                        console.log('🔥 Current isFullScreenCart state:', isFullScreenCart);
                        console.log('🔥 About to call handleTakePayment');
                        handleTakePayment();
                      }}
                      className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-white/20 flex items-center justify-center">
                          <CreditCard className="h-5 w-5" />
                        </div>
                        <div className="text-left">
                          <div className="font-bold">Take Payment</div>
                          <div className="text-sm text-green-100">Process payment now</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">${cartTotal.toFixed(2)}</div>
                        <div className="text-xs text-green-100">Immediate</div>
                      </div>
                    </button>

                    <button
                      onClick={handleSendInvoice}
                      className="w-full flex items-center justify-between p-4 bg-white/80 border border-gray-300/50 text-gray-700 rounded-xl hover:bg-gray-50/80 transition-all duration-200 shadow-md"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                          <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div className="text-left">
                          <div className="font-bold">Send Invoice</div>
                          <div className="text-sm text-gray-500">Email invoice to customer</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">${cartTotal.toFixed(2)}</div>
                        <div className="text-xs text-gray-500">Pay later</div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Product Sale Payment Modal */}
      <RecordProductSalePaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={handlePaymentSuccess}
        businessId={selectedBusiness?.id || ''}
        cart={cart}
        total={cartTotal}
        cartTotals={cartTotals}
        discounts={discounts}
        selectedCustomer={selectedCustomer}
      />
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      
      {/* Select Customer Modal */}
      <SelectCustomerModal
        isOpen={isSelectCustomerModalOpen}
        onClose={() => setIsSelectCustomerModalOpen(false)}
        onSelect={(customer) => {
          console.log('👤 Customer selected for sale:', customer);
          setSelectedCustomer(customer);
          setIsSelectCustomerModalOpen(false);
        }}
      />

      {/* Send Invoice Modal - Render in separate portal */}
      {createPortal(
        <SendInvoiceModal
          isOpen={showSendInvoiceModal}
          onClose={() => {
            console.log('🔍 SendInvoiceModal onClose called');
            setShowSendInvoiceModal(false);
          }}
          onSelectMethod={handleSendInvoiceMethod}
          customerEmail={selectedCustomer?.email}
          customerPhone={selectedCustomer?.phone}
          hasStripeConnected={!!(selectedBusiness?.stripe_connect_account_id && selectedBusiness?.online_payments_enabled)}
          isLoading={isSendingInvoice}
          loadingMethod={isSendingInvoice ? 'email' : null}
        />,
        document.body
      )}
    </>
  );
}
